/**
 * Shared number/currency formatting helpers for tiles.
 */

export type CompactNotation = 'standard' | 'compact';
export type ValueFormatMode = '' | 'number' | 'currency' | 'percent';

export interface NumericFormatSettings {
  valueFormat?: ValueFormatMode | string | Record<string, any> | ((v: number) => string);
  currency?: string;

  // New controls
  numberNotation?: CompactNotation; // default: 'standard'
  compactThreshold?: number; // default: 1000
  compactDecimals?: number; // default: 1
  prefixCurrencyOnCompact?: boolean; // default: true
}

const DEFAULT_THRESHOLD = 1000;
const DEFAULT_DECIMALS = 1;

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function trimTrailingZeros(n: string): string {
  return n.replace(/(\.\d*?[1-9])0+$/,'$1').replace(/\.0+$/,'');
}

function formatCompactCore(value: number, decimals = DEFAULT_DECIMALS): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (abs >= 1_000_000_000) return `${sign}${trimTrailingZeros((abs / 1_000_000_000).toFixed(decimals))}B`;
  if (abs >= 1_000_000) return `${sign}${trimTrailingZeros((abs / 1_000_000).toFixed(decimals))}M`;
  if (abs >= 1_000) return `${sign}${trimTrailingZeros((abs / 1_000).toFixed(decimals))}K`;
  return `${value}`;
}

/**
 * Backward-compatible helper you already used in a few tiles.
 */
export const formatNumberWithSuffix = (value: number): string => {
  if (!isFiniteNumber(value)) return String(value);
  return formatCompactCore(value, 2);
};

export function makeSmartValueFormatter(settings: NumericFormatSettings = {}) {
  const valueFormat = settings?.valueFormat;
  const currency = settings?.currency || 'USD';

  const notation: CompactNotation = settings?.numberNotation === 'compact' ? 'compact' : 'standard';
  const compactThreshold =
    typeof settings?.compactThreshold === 'number' && Number.isFinite(settings.compactThreshold)
      ? Math.max(0, settings.compactThreshold)
      : DEFAULT_THRESHOLD;
  const compactDecimals =
    typeof settings?.compactDecimals === 'number' && Number.isFinite(settings.compactDecimals)
      ? Math.max(0, Math.min(6, settings.compactDecimals))
      : DEFAULT_DECIMALS;
  const prefixCurrencyOnCompact = settings?.prefixCurrencyOnCompact !== false;

  // Pass-through function (highest precedence)
  if (typeof valueFormat === 'function') {
    return (v: number) => valueFormat(v);
  }

  // Nivo-like d3 format string pass-through (caller may handle)
  if (typeof valueFormat === 'string' && valueFormat.startsWith('.')) {
    return (v: number) => String(v);
  }

  // If object format options, honor them via Intl NumberFormat
  if (valueFormat && typeof valueFormat === 'object' && !Array.isArray(valueFormat)) {
    try {
      const nf = new Intl.NumberFormat(undefined, valueFormat as Intl.NumberFormatOptions);
      return (v: number) => nf.format(v);
    } catch {
      return (v: number) => String(v);
    }
  }

  // Determine mode
  const mode: ValueFormatMode =
    valueFormat === 'currency' || valueFormat === 'number' || valueFormat === 'percent'
      ? valueFormat
      : '';

  const numberFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
  const currencyFormatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  });
  const percentFormatter = new Intl.NumberFormat(undefined, {
    style: 'percent',
    maximumFractionDigits: 2,
  });

  return (raw: number) => {
    const v = Number(raw);
    if (!Number.isFinite(v)) return String(raw);

    // Compact path
    if (notation === 'compact' && Math.abs(v) >= compactThreshold) {
      const compact = formatCompactCore(v, compactDecimals);
      if (mode === 'currency' && prefixCurrencyOnCompact) {
        // simple symbol fallback
        const symbol = currency === 'USD' ? '$' : `${currency} `;
        // preserve negative sign from compact core
        if (compact.startsWith('-')) return `-${symbol}${compact.slice(1)}`;
        return `${symbol}${compact}`;
      }
      if (mode === 'percent') {
        // for compact+percent, keep percent formatter semantics
        return percentFormatter.format(v);
      }
      return compact;
    }

    // Standard path
    if (mode === 'currency') return currencyFormatter.format(v);
    if (mode === 'number') return numberFormatter.format(v);
    if (mode === 'percent') return percentFormatter.format(v);

    // default generic
    return numberFormatter.format(v);
  };
}